import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

// Helpers to work with local dates (ignore time) and periods
const getPeriodStart = (d, period = 'daily') => {
	if (!d) return null;
	const dt = new Date(d);
	if (isNaN(dt)) return null;
	if (period === 'daily') {
		return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
	}
	if (period === 'weekly') {
		// ISO week start (Monday)
		const day = dt.getDay(); // 0 (Sun) - 6 (Sat)
		const isoDay = day === 0 ? 6 : day - 1; // 0 -> Monday index 0
		return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() - isoDay);
	}
	if (period === 'monthly') {
		return new Date(dt.getFullYear(), dt.getMonth(), 1);
	}
	// default to daily
	return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
};

const formatPeriodKey = (d, period) => {
	const s = getPeriodStart(d, period);
	if (!s) return null;
	if (period === 'daily')
		return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(
			2,
			'0'
		)}`;
	if (period === 'weekly') {
		// Use the ISO-week's Monday start as the key (YYYY-MM-DD of the Monday)
		return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(
			2,
			'0'
		)}`;
	}
	if (period === 'monthly') return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}`;
	return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}`;
};

const isSamePeriod = (isoA, isoB, period) => {
	if (!isoA || !isoB) return false;
	const a = formatPeriodKey(isoA, period);
	const b = formatPeriodKey(isoB, period);
	return a === b;
};

const isPreviousPeriod = (isoPrev, isoCurrent, period) => {
	if (!isoPrev || !isoCurrent) return false;
	const startCurrent = getPeriodStart(isoCurrent, period);
	if (!startCurrent) return false;
	if (period === 'daily') {
		const prev = new Date(startCurrent.getFullYear(), startCurrent.getMonth(), startCurrent.getDate() - 1);
		return formatPeriodKey(prev, period) === formatPeriodKey(isoPrev, period);
	}
	if (period === 'weekly') {
		const prev = new Date(startCurrent.getFullYear(), startCurrent.getMonth(), startCurrent.getDate() - 7);
		return formatPeriodKey(prev, period) === formatPeriodKey(isoPrev, period);
	}
	if (period === 'monthly') {
		const prev = new Date(startCurrent.getFullYear(), startCurrent.getMonth() - 1, 1);
		return formatPeriodKey(prev, period) === formatPeriodKey(isoPrev, period);
	}
	return false;
};

const StreakResolutionUI = ({
	resolution,
	isUpdateAllowed,
	isDeleteAllowed,
	isUpdating,
	onUpdate,
	onDelete,
	onDecrypt,
}) => {
	const { t } = useLanguage();

	const period = resolution.period || 'daily';
	const loggedToday = resolution.lastLogDate
		? isSamePeriod(resolution.lastLogDate, new Date().toISOString(), period)
		: false;

	const lastIsBeforePrevious = resolution.lastLogDate
		? !isSamePeriod(resolution.lastLogDate, new Date().toISOString(), period) &&
		  !isPreviousPeriod(resolution.lastLogDate, new Date().toISOString(), period)
		: true;

	const displayedCurrentStreak = lastIsBeforePrevious ? 0 : resolution.currentStreak || 0;

	const inARowKey = period === 'daily' ? 'dailyInARow' : period === 'weekly' ? 'weeklyInARow' : 'monthlyInARow';

	const handleLog = () => {
		if (!isUpdateAllowed) return;

		const todayIso = new Date().toISOString();
		const periodType = resolution.period || 'daily';
		let newCurrent = 1;
		let newLongest = resolution.longestStreak || 0;

		if (resolution.lastLogDate && isPreviousPeriod(resolution.lastLogDate, todayIso, periodType)) {
			newCurrent = (resolution.currentStreak || 0) + 1;
		} else {
			// last log was before previous period or absent -> reset to 1
			newCurrent = 1;
		}

		if (newCurrent > newLongest) newLongest = newCurrent;

		const updated = {
			...resolution,
			currentStreak: newCurrent,
			longestStreak: newLongest,
			lastLogDate: todayIso,
			// Optionally keep logs array for history in Firestore; not shown in UI
			logs: Array.isArray(resolution.logs) ? [...resolution.logs, { date: todayIso }] : [{ date: todayIso }],
		};

		onUpdate(resolution.id, updated, t);
	};

	return (
		<div className='bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow'>
			<div className='mb-3'>
				<h3 className='text-lg font-semibold text-gray-900 mb-1'>{resolution.text}</h3>
				{resolution.isEncrypted && (
					<span className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800'>
						🔒 {t('private')}
					</span>
				)}
			</div>

			<div className='mb-4'>
				<div className='flex items-center justify-between'>
					<div className='flex items-center space-x-4'>
						<div>
							<div className='text-sm text-gray-600'>{t('currentStreak')}</div>
							<div className='text-4xl sm:text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-blue-600 drop-shadow-md'>
								{displayedCurrentStreak}
							</div>
							<div className='text-xs text-gray-500 mt-1'>{t(inARowKey)}</div>
						</div>
					</div>
					<div className='text-right'>
						<div className='text-sm text-gray-600'>{t('longestStreak')}</div>
						<div className='inline-flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full shadow-sm mt-1'>
							<span className='text-lg font-semibold text-gray-800'>{resolution.longestStreak || 0}</span>
							<span className='text-sm text-gray-500'>🏆</span>
						</div>
					</div>
				</div>
			</div>

			<div className='mb-4'>
				<div className='flex items-center justify-between'>
					<div className='text-sm text-gray-500'>
						<div className='text-xs text-gray-600'>{t('lastLogDate')}</div>
						<div>
							{resolution.lastLogDate
								? new Date(resolution.lastLogDate).toLocaleDateString()
								: t('never')}
						</div>
					</div>
					<div className='flex items-center space-x-2'>
						{isUpdateAllowed && !loggedToday && (
							<button
								onClick={handleLog}
								disabled={isUpdating}
								className={`px-3 py-1 text-white rounded-md text-sm ${
									isUpdating ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'
								}`}
							>
								+1
							</button>
						)}
						{resolution.isEncrypted && (
							<button
								onClick={() => onDecrypt(resolution.id)}
								className='px-3 py-1 bg-yellow-500 text-white rounded-md text-sm hover:bg-yellow-600'
							>
								🔓 {t('decrypt')}
							</button>
						)}
					</div>
				</div>
			</div>

			<div className='flex justify-end'>
				{isDeleteAllowed && (
					<button
						onClick={() => onDelete(resolution.id, t)}
						className='px-3 py-1 bg-red-500 text-white rounded-md text-sm hover:bg-red-600'
					>
						🗑️ {t('delete')}
					</button>
				)}
			</div>
		</div>
	);
};

export default StreakResolutionUI;
